import React from 'react';
import {
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  BusinessPartnerRole,
  BusinessPartnerType,
} from '../../types';
import Button from '../ui/Button';
import {
  XMarkIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  TagIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface PartnerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: {
    id: string;
    companyName: string;
    address: string;
    contactPerson: string;
    email: string;
    phone: string;
    status: 'Active' | 'Inactive';
    category: BusinessPartnerCategory;
    source: 'client' | 'businessPartner';
    dataSource: BusinessPartnerDataSource;
    displayName?: string;
    originalType?: BusinessPartnerType;
  } | null;
  businessPartner?: BusinessPartner; // BusinessPartner型の詳細情報（拡張表示用）
  companyName?: string; // 登録会社名（独自登録データの場合）
  onEdit?: (partner: BusinessPartner) => void; // 編集ボタンのコールバック
  onDelete?: (partnerId: string) => void; // 削除ボタンのコールバック
}

const getCategoryLabel = (category: BusinessPartnerCategory) => {
  switch (category) {
    case BusinessPartnerCategory.DEALERSHIP:
      return '売買・買取店';
    case BusinessPartnerCategory.AUCTION:
      return 'オークション会場';
    case BusinessPartnerCategory.OTHER:
      return 'その他';
    default:
      return '不明';
  }
};

const getCategoryBadge = (category: BusinessPartnerCategory) => {
  switch (category) {
    case BusinessPartnerCategory.DEALERSHIP:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case BusinessPartnerCategory.AUCTION:
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case BusinessPartnerCategory.OTHER:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getStatusBadge = (status: 'Active' | 'Inactive') => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'Inactive':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getRoleLabel = (role: BusinessPartnerRole) => {
  switch (role) {
    case BusinessPartnerRole.BILLING:
      return '請求先';
    case BusinessPartnerRole.PICKUP:
      return '引き取り先';
    case BusinessPartnerRole.DELIVERY:
      return '搬入先';
    default:
      return '不明';
  }
};

const getTypeLabel = (type: BusinessPartnerType) => {
  switch (type) {
    case BusinessPartnerType.CORPORATE:
      return '法人';
    case BusinessPartnerType.INDIVIDUAL:
      return '個人';
    default:
      return '不明';
  }
};

export const PartnerDetailModal: React.FC<PartnerDetailModalProps> = ({
  isOpen,
  onClose,
  partner,
  businessPartner,
  companyName,
  onEdit,
  onDelete,
}) => {
  if (!isOpen || !partner) return null;

  // BusinessPartner型の詳細情報が利用可能かチェック
  const hasDetailedInfo = businessPartner && partner.source === 'businessPartner';

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '不明';
    }
  };

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen p-4'>
        {/* オーバーレイ */}
        <div
          className='fixed inset-0 bg-gray-500/75 transition-opacity duration-300 ease-out'
          onClick={onClose}
          aria-hidden='true'
        />

        {/* モーダル */}
        <div className='relative bg-white dark:bg-gray-800 rounded-lg p-6 text-left overflow-hidden shadow-xl transform transition-all duration-300 ease-out scale-100 opacity-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
          {/* ヘッダー */}
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center space-x-3'>
              {partner.dataSource === BusinessPartnerDataSource.CUSTOM ? (
                <UserIcon className='h-6 w-6 text-green-600' title='独自追加データ' />
              ) : (
                <BuildingOffice2Icon className='h-6 w-6 text-blue-500' title='ラクハイ提供データ' />
              )}
              <h2 className='text-xl font-bold text-gray-900 dark:text-white'>取引先詳細情報</h2>
            </div>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
              aria-label='モーダルを閉じる'
              title='モーダルを閉じる'
            >
              <XMarkIcon className='h-5 w-5' />
            </button>
          </div>

          {/* コンテンツ */}
          <div className='space-y-6'>
            {/* 基本情報 */}
            <div className='border-b border-gray-200 dark:border-gray-700 pb-6'>
              <div className='flex items-start justify-between mb-4'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
                    {partner.source === 'businessPartner' && partner.displayName
                      ? partner.displayName
                      : partner.companyName}
                  </h3>
                  {partner.source === 'businessPartner' && partner.displayName && (
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                      正式名称: {partner.companyName}
                    </p>
                  )}
                </div>
                <div className='flex flex-col items-end space-y-2'>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                      partner.status
                    )}`}
                  >
                    {partner.status === 'Active' ? (
                      <>
                        <CheckCircleIcon className='h-3 w-3 inline mr-1' />
                        有効
                      </>
                    ) : (
                      <>
                        <XCircleIcon className='h-3 w-3 inline mr-1' />
                        無効
                      </>
                    )}
                  </span>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryBadge(
                      partner.category
                    )}`}
                  >
                    <TagIcon className='h-3 w-3 inline mr-1' />
                    {getCategoryLabel(partner.category)}
                  </span>
                  {partner.dataSource === BusinessPartnerDataSource.CUSTOM && (
                    <span className='px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'>
                      <UserIcon className='h-3 w-3 inline mr-1' />
                      独自追加
                    </span>
                  )}
                </div>
              </div>

              {/* 連絡先情報 */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='flex items-start space-x-3'>
                  <MapPinIcon className='h-5 w-5 text-gray-400 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white'>住所</p>
                    <p className='text-sm text-gray-600 dark:text-gray-300'>{partner.address}</p>
                  </div>
                </div>
                <div className='flex items-start space-x-3'>
                  <PhoneIcon className='h-5 w-5 text-gray-400 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white'>電話番号</p>
                    <p className='text-sm text-gray-600 dark:text-gray-300'>
                      <a
                        href={`tel:${partner.phone}`}
                        className='hover:text-primary-600 dark:hover:text-primary-400 transition-colors'
                      >
                        {partner.phone}
                      </a>
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-3'>
                  <UserIcon className='h-5 w-5 text-gray-400 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white'>担当者</p>
                    <p className='text-sm text-gray-600 dark:text-gray-300'>
                      {partner.contactPerson}
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-3'>
                  <EnvelopeIcon className='h-5 w-5 text-gray-400 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white'>
                      メールアドレス
                    </p>
                    <p className='text-sm text-gray-600 dark:text-gray-300'>
                      <a
                        href={`mailto:${partner.email}`}
                        className='hover:text-primary-600 dark:hover:text-primary-400 transition-colors'
                      >
                        {partner.email}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* BusinessPartner詳細情報（利用可能な場合のみ） */}
            {hasDetailedInfo && businessPartner && (
              <div className='space-y-4'>
                <h4 className='text-md font-semibold text-gray-900 dark:text-white flex items-center'>
                  <InformationCircleIcon className='h-5 w-5 mr-2' />
                  詳細情報
                </h4>

                {/* 事業者種別・対応役割 */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white mb-2'>
                      事業者種別
                    </p>
                    <span className='px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'>
                      {getTypeLabel(businessPartner.type)}
                    </span>
                  </div>
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white mb-2'>
                      対応可能役割
                    </p>
                    <div className='flex flex-wrap gap-1'>
                      {businessPartner.availableRoles.map(role => (
                        <span
                          key={role}
                          className='px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                        >
                          {getRoleLabel(role)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 拠点情報 */}
                {businessPartner.locations.length > 1 && (
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white mb-3'>
                      拠点一覧
                    </p>
                    <div className='space-y-2'>
                      {businessPartner.locations.map(location => (
                        <div
                          key={location.id}
                          className='p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'
                        >
                          <div className='flex items-center justify-between mb-2'>
                            <h5 className='text-sm font-medium text-gray-900 dark:text-white'>
                              {location.name}
                              {location.isDefault && (
                                <span className='ml-2 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'>
                                  メイン拠点
                                </span>
                              )}
                            </h5>
                            <div className='flex flex-wrap gap-1'>
                              {location.roles.map(role => (
                                <span
                                  key={role}
                                  className='px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                >
                                  {getRoleLabel(role)}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300'>
                            <div className='flex items-center space-x-2'>
                              <MapPinIcon className='h-3 w-3' />
                              <span>{location.address}</span>
                            </div>
                            <div className='flex items-center space-x-2'>
                              <PhoneIcon className='h-3 w-3' />
                              <span>{location.phone}</span>
                            </div>
                            <div className='flex items-center space-x-2'>
                              <UserIcon className='h-3 w-3' />
                              <span>{location.contactPerson}</span>
                            </div>
                            {location.email && (
                              <div className='flex items-center space-x-2'>
                                <EnvelopeIcon className='h-3 w-3' />
                                <span>{location.email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 備考 */}
                {businessPartner.notes && (
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-white mb-2'>備考</p>
                    <div className='p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'>
                      <p className='text-sm text-gray-600 dark:text-gray-300'>
                        {businessPartner.notes}
                      </p>
                    </div>
                  </div>
                )}

                {/* メタデータ */}
                <div className='pt-4 border-t border-gray-200 dark:border-gray-700'>
                  <p className='text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center'>
                    <ClockIcon className='h-3 w-3 mr-1' />
                    更新情報
                  </p>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400'>
                    <div>
                      <span className='font-medium'>作成日時:</span>
                      <span className='ml-2'>{formatDate(businessPartner.createdAt)}</span>
                    </div>
                    <div>
                      <span className='font-medium'>更新日時:</span>
                      <span className='ml-2'>{formatDate(businessPartner.updatedAt)}</span>
                    </div>
                    <div>
                      <span className='font-medium'>システム事前設定:</span>
                      <span className='ml-2'>
                        {businessPartner.isSystemPreset ? 'はい' : 'いいえ'}
                      </span>
                    </div>
                    {businessPartner.dataSource === BusinessPartnerDataSource.CUSTOM &&
                      companyName && (
                        <div className='md:col-span-2'>
                          <span className='font-medium'>登録会社:</span>
                          <span className='ml-2'>{companyName}</span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Client型の場合の基本メタデータ */}
            {!hasDetailedInfo && (
              <div className='pt-4 border-t border-gray-200 dark:border-gray-700'>
                <p className='text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center'>
                  <ClockIcon className='h-3 w-3 mr-1' />
                  基本情報
                </p>
                <div className='text-xs text-gray-500 dark:text-gray-400'>
                  <div>
                    <span className='font-medium'>データソース:</span>
                    <span className='ml-2'>
                      {partner.dataSource === BusinessPartnerDataSource.CUSTOM
                        ? '独自追加'
                        : 'ラクハイ提供'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* フッター */}
          <div className='mt-8 flex justify-between'>
            {/* 左側：編集・削除ボタン */}
            <div className='flex space-x-3'>
              {onEdit &&
                businessPartner &&
                businessPartner.dataSource !== BusinessPartnerDataSource.CUSTOM && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => onEdit(businessPartner)}
                    icon={PencilIcon}
                    className='text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500'
                  >
                    編集
                  </Button>
                )}
              {onDelete &&
                businessPartner &&
                businessPartner.dataSource !== BusinessPartnerDataSource.CUSTOM && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => onDelete(businessPartner.id)}
                    icon={TrashIcon}
                    className='text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-600'
                  >
                    削除
                  </Button>
                )}
            </div>

            {/* 右側：閉じるボタン */}
            <button
              onClick={onClose}
              className='px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
